<template>
  <div id="third-page" class="w-full space-y-8">
    <!-- Citation Example -->
    <section
      class="bg-white/90 backdrop-blur-md rounded-2xl shadow-md p-6 border border-gray-100 bg-gradient-to-br from-spicy-mustard-100 to-mocha-mousse-50"
    >
      <div class="text-left space-y-2">
        <div class="flex items-center gap-2">
          <p class="font-semibold text-lg">Citation Example</p>

          <!-- Info tooltip -->
          <div class="relative group inline-block">
            <Icon
              icon="mdi:information-outline"
              class="w-4 h-4 text-gray-400 hover:text-mocha-mousse cursor-help transition-colors"
            />
            <div
              class="absolute left-1/2 -translate-x-1/2 top-6 w-72 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 shadow-xl z-50"
            >
              This is a sample citation. The final DOI and citation will be
              generated after submission.
            </div>
          </div>
        </div>

        <p class="text-xs italic text-gray-500">
          The real citation and DOI will be displayed after submission
          confirmation.
        </p>

        <p
          id="citation-text"
          class="mt-3 text-sm bg-gray-50 border border-gray-200 p-3 rounded-lg"
        >
          {{ createFakeCitation() }}
        </p>
      </div>
    </section>

    <!-- File Structure -->
    <section
      class="bg-white/90 backdrop-blur-md rounded-2xl shadow-md p-6 border border-gray-100 bg-gradient-to-br from-spicy-mustard-100 to-mocha-mousse-50"
    >
      <h3 class="font-semibold mb-3 flex items-center gap-2">
        <Icon icon="mdi:file-tree" class="text-mocha-mousse w-5 h-5" />
        File Structure
      </h3>
      <FileStructureComponent :structure_dict="formState.structureDict.value" />
    </section>

    <!-- Description Preview -->
    <section
      class="bg-white/90 backdrop-blur-md rounded-2xl shadow-md p-6 border border-gray-100 bg-gradient-to-br from-spicy-mustard-100 to-mocha-mousse-50"
    >
      <div class="text-left space-y-3">
        <div class="flex items-center gap-2 font-semibold">
          <span>Description</span>
          <div class="relative group inline-block">
            <Icon
              icon="mdi:information-outline"
              class="w-4 h-4 text-gray-400 hover:text-mocha-mousse cursor-help transition-colors"
            />
            <div
              class="absolute left-1/2 -translate-x-1/2 top-6 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 shadow-xl z-50"
            >
              Read-only description of your dataset before submission.
            </div>
          </div>
        </div>

        <textarea
          class="w-full text-sm text-gray-700 border-2 border-gray-200 rounded-xl p-3 resize-none bg-gray-50"
          rows="10"
          disabled
          v-model="formState.description"
        ></textarea>
      </div>
    </section>

    <!-- Navigation Buttons -->
    <div class="my-8 w-full flex justify-between items-center">
      <button
        class="px-6 py-2 cursor-pointer bg-gradient-to-r from-desert-flower-100 to-arabesque-200 font-medium rounded-lg shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
        @click="goToPrevPage"
      >
        <Icon
          icon="mdi:arrow-left"
          class="w-5 h-5 group-hover:translate-x-1 transition-transform"
        />
        <span>Back</span>
      </button>

      <button
        class="group relative px-8 py-3 cursor-pointer bg-gradient-to-r from-mint-green-100 to-periwinkle-100 font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
        @click="goToNextPage"
      >
        <span>Next</span>
        <Icon
          icon="mdi:arrow-right"
          class="w-5 h-5 group-hover:translate-x-1 transition-transform"
        />
      </button>
    </div>

    <!-- Confirmation Modal
    <BaseModal :size="'lg'" :visible="showModal" @close="closeModal">
      <div class="flex flex-col gap-4">
        <h2 class="font-semibold text-2xl text-gray-900">Confirm Submission</h2>

        <p class="text-sm leading-relaxed text-gray-700">
          Are you sure you want to continue with the upload? This operation is
          <strong>final</strong>. To modify the uploaded data later, you’ll need
          to open a <strong>VUO ticket</strong>.
        </p>

        <div class="flex justify-between pt-2">
          <button
            class="px-6 py-2 bg-gradient-to-r from-desert-flower-100 to-arabesque-200 font-medium rounded-lg shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
            @click="closeModal"
          >
            <span>Back to Edit</span>
          </button>

          <button
            class="group relative px-8 py-3 cursor-pointer bg-gradient-to-r from-mint-green-100 to-periwinkle-100 font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
            @click="confirmSubmit"
          >
            <span>Confirm</span>
          </button>
        </div>
      </div>
    </BaseModal> -->
  </div>
</template>

<script setup>
import { onMounted, ref, computed } from "vue";
import FileStructureComponent from "@/components/FileStructure/FileStructureComponent.vue";
import BaseModal from "../components/BaseModal.vue";
import { useFormStore } from "@/stores/FormStore";
import { useRouter } from "vue-router";
import { useLogger } from "@/stores/useLogger";
import { Icon } from "@iconify/vue";
import { useLoadingService } from "@/services/LoadingService";
import { useModalService } from "@/services/ModalService";

const logger = useLogger();
logger.wireLifecycle();

const router = useRouter();
const formStore = useFormStore();
const formState = formStore.formState;

const showModal = ref(false);
const modalSvc = useModalService();

const loadingSvc = useLoadingService();
// keep title normalized (optional, but tidy)
onMounted(() => {
  const taskId = "third-form-page-initializzazion";
  loadingSvc.start(taskId, { title: "Initializzazate Page" });

  if (formState.title) formStore.sanitizeTitle();
  loadingSvc.stop(taskId);
});

const citation = computed(() => createFakeCitation());

// --- Citation helpers ---
function createFakeCitation() {
  const title = `Dataset of ${formState.title || ""}`;
  const doi_url = "http://doi.org/1111.111";

  const authors = (formState.uploaders || [])
    .map((author, idx, arr) => formatAuthor(author, idx, arr.length))
    .join("");

  const currentYear = new Date().getFullYear();
  return `${authors} (${currentYear}). ${title} [Data set]. Elettra Sincrotrone Trieste. ${doi_url}`;
}

function formatAuthor(author = {}, idx, total) {
  const lastname = capitalize(author.lastname);
  const name = capitalize(author.name);

  if (idx === total - 2) return `${lastname} ${name} and `;
  if (idx !== total - 1) return `${lastname} ${name}, `;
  return `${lastname} ${name}`;
}

function capitalize(str = "") {
  return str
    .split(" ")
    .filter(Boolean)
    .map((el) => el.charAt(0).toUpperCase() + el.slice(1).toLowerCase())
    .join(" ");
}

// --- Navigation ---
function goToPrevPage() {
  logger.log("button_clicked", { metadata: { button: "back_to_extra" } });
  router.push({ name: "extra" });
}

function goToNextPage() {
  modalSvc.show({
    title: "Confirm Submission",
    message:
      "Are you sure you want to continue with the upload? This operation is final.",
    icon: "mdi:alert-circle-outline",
    buttons: [
      {
        text: "Cancel",
        variant: "secondary",
        action: () => {
          logger.log("button_clicked", {
            metadata: { button: "close_submit_modal" },
          });
        },
      },
      {
        text: "Confirm",
        variant: "primary",
        action: () => {
          logger.log("button_clicked", {
            metadata: { button: "close_submit_modal" },
          });
          router.push({ name: "confirmation" });
        },
      },
    ],
  });
}
</script>
